import { useSelector } from 'react-redux'
import { FoundationsEnums } from '../../enum'
import { RunVisualUnitWithId_VisualUnit } from '../../Interfaces/APIContracts/RunVisualUnitWithId_VisualUnit'
import { VisualUnitApiDTO_VisualUnit } from '../../Interfaces/APIContracts/VisualUnitApiDTO_VisualUnit'
import ReportChart from '../Charts/ReportChart'
import BuildQuery from '../VisualUnitParams/BuildQuery'
import ActivePanel from './ActivePanel'
import { VUChartSetting } from './SettingToolbar'
import { ReportFailed } from './ReportFailed'
import React, { useEffect, useState } from 'react'
import { ChartConfig } from '../../Redux/VisualUnits/visualUnitStateSlice'
import { ReportTable } from './ReportTable'
import SingleValueReportVU from './SingleValueReportVU'
import { VisualUnitApiDTO_VisualUnit_Extended } from '../../Interfaces/VisualUnit/VisualUnitApiDTO_VisualUnit'
import { RunVisualUnitWithId_VisualUnit_Extended } from '../../Interfaces/VisualUnit/RunVisualUnitWithId_VisualUnit'

export interface ReportProps {
    report?:VisualUnitApiDTO_VisualUnit
    tempVuData: VisualUnitApiDTO_VisualUnit_Extended
    chartComponent: any
    executeResult: RunVisualUnitWithId_VisualUnit_Extended
    isCollapsed: boolean
    reportFailed: boolean
    redrawReport: boolean
    tableJSOnData?: { [s: string]: any }
    operationStatusMessage: string
    isExplore: boolean
    setTableJSOnData?: (data: { [s: string]: any }) => void
    openDrillThru: (cellInfo: any, cell: any, row: any, rowIndex: any) => void,
    toggleIsExplore: Function
}

// DECIDE WETHER TO RENDER A TABLE, CHART, STAT PORTAL, WORKLOAD MANAGER, LMS OR OTHER VISUAL UNIT
export const ReportEl = React.memo(
    (props: ReportProps) => {
        const {
            report,
            tempVuData,
            chartComponent,
            executeResult,
            isCollapsed,
            reportFailed,
            tableJSOnData,
            operationStatusMessage,
            isExplore,
            setTableJSOnData,
            openDrillThru,
            toggleIsExplore
        } = props
        const [chartConfig, setChartConfig] = useState({
            enable3dView: false,
            isFullScreen: false,
            isDrawing: false,
            showLegend: true,
        } as ChartConfig)

        const updateChartConfig = (key: keyof ChartConfig, newValue: any) => {
            setChartConfig({ ...chartConfig, [key]: newValue })
        }
        const ReportByType = (childProps: {}) => {
            if(tempVuData.ViewerType != FoundationsEnums.VisualUnitViewerType.ActivePanel)
                if ((reportFailed || operationStatusMessage !== '')) {
                    return (
                        <ReportFailed
                            VisualUnitId={tempVuData.VisualUnitId}
                            isExplore={isExplore}
                            toggleIsExplore={toggleIsExplore}
                            allowEdit={executeResult.IsEditAllowed}
                            operationStatusMessage={operationStatusMessage}
                        />
                    )
                }
            if (tempVuData.BuildQuery) {
                return <BuildQuery buildQueryText={executeResult.sqlString} />
            }
            switch (tempVuData.ViewerType) {
                case FoundationsEnums.VisualUnitViewerType.SVU:
                case FoundationsEnums.VisualUnitViewerType.WorkList:
                case FoundationsEnums.VisualUnitViewerType.WorkListWithoutGauge:
                case FoundationsEnums.VisualUnitViewerType.Gauge:
                    if (tempVuData.XAxisList.length > 0 || tempVuData.YAxisList.length > 0) return <span> Bad report data configuration </span>
                    return <SingleValueReportVU
                        drillThruDpList={executeResult.drillThruDPList}
                        openDrillThru={openDrillThru}
                        vuId={0} 
                        tableJSOnData={tableJSOnData}
                        />
                case FoundationsEnums.VisualUnitViewerType.Table:
                    if (tempVuData.XAxisList.length === 0 && tempVuData.YAxisList.length === 0) return <span> Bad report data configuration </span>
                    return (
                        <ReportTable
                            MeasureList={tempVuData?.MeasureList} 
                            AllowDrillThru={ tempVuData?.AllowDrillThru}
                            yAxisList={tempVuData.YAxisList}
                            xAxisList={tempVuData.XAxisList}
                            openDrillThru={openDrillThru}
                            vuId={tempVuData.VisualUnitId}
                            drillThruDPList={executeResult.drillThruDPList}
                            tableJSOnData={tableJSOnData || {}}
                            setTableJSOnData={setTableJSOnData || (() => { })}
                        />
                    )
                case FoundationsEnums.VisualUnitViewerType.ActivePanel:
                    return <ActivePanel report={tempVuData} />
                case FoundationsEnums.VisualUnitViewerType.Chart_Bar:
                case FoundationsEnums.VisualUnitViewerType.Chart_ColumnGroup:
                case FoundationsEnums.VisualUnitViewerType.Chart_StackedColumn:
                case FoundationsEnums.VisualUnitViewerType.Chart_Line:
                case FoundationsEnums.VisualUnitViewerType.Chart_Pie:
                case FoundationsEnums.VisualUnitViewerType.Chart_Scatter:
                case FoundationsEnums.VisualUnitViewerType.Chart_StackedBar:
                    // ensure chart data is defined before rendering the chart
                    if (executeResult?.chartData === undefined)  return <span> Bad report data configuration </span>
                    if (tempVuData.XAxisList.length === 0 && tempVuData.YAxisList.length === 0) return <span> Bad report data configuration </span>
                    return (
                        <ReportChart
                            vuId={tempVuData.VisualUnitId}
                            viewerType={executeResult.viewerType}
                            chartComponent={chartComponent}
                            executeResult={executeResult}
                            chartConfig={chartConfig}
                            updateChartConfig={updateChartConfig}
                            openDrillThru={openDrillThru}
                            AllowDrillThru = {tempVuData.AllowDrillThru}
                        />
                    )
                default:
                    return (
                        <ReportFailed
                            VisualUnitId={tempVuData.VisualUnitId}
                            allowEdit={executeResult.IsEditAllowed}
                            operationStatusMessage={operationStatusMessage}
                            isExplore={isExplore} 
                            toggleIsExplore={toggleIsExplore}
                        />
                    )
            }
        }
        return (
            <>
                {/* SETTINGS LIST */}
                <VUChartSetting
                    vuId={tempVuData.VisualUnitId}
                    executeResult={executeResult}
                    isExplore={isExplore}
                    isCollapsed={isCollapsed}
                    chartConfig={chartConfig}
                    updateChartConfig={updateChartConfig}
                />
                {/* END SETTINGS LIST */}
                {/* VISUAL UNIT WRAPPER */}
                <ReportByType />
                {/* END VISUAL UNIT WRAPPER */}
            </>
        )
    },
    // We only redraw the report after an execute, vuConfiguration, or preview API call is made. This prevents random rerenders anytime any prop changes
    //  (report and executeResult are objects so the dfault equality check react.memo uses on them will always retrun false )
    (prevProps, nextProps) => {
        return prevProps.redrawReport === nextProps.redrawReport && prevProps.operationStatusMessage === nextProps.operationStatusMessage 
    }
)

export default ReportEl